import { Document } from '@tiptap/extension-document'
import { Mention } from '@tiptap/extension-mention'
import { Paragraph } from '@tiptap/extension-paragraph'
import { Text } from '@tiptap/extension-text'
import { Placeholder } from '@tiptap/extensions'
import { EditorContent, useEditor } from '@tiptap/react'
import { useMemo, useRef } from 'react'
import { createMentionSuggestionRenderer } from './mentionSuggestionRenderer'
import { serializeMentionText } from './mentionText'
import * as styles from './styles'
import { userMentionItems } from './userMentions'

type MentionComposerProps = {
  /** Called with the comment text — mentions already as markup — on every edit. */
  onChange: (comment: string) => void
  /**
   * Called on Cmd/Ctrl+Enter. Fires unconditionally, so it must apply the same
   * guards the Save button does.
   */
  onSubmit: () => void
}

/**
 * The feedback comment input: plain text plus `@` mentions of workspace users.
 * What it reports is exactly what gets stored, so the server sees the mention
 * markup it needs to notify the people named.
 */
export const MentionComposer: React.VFC<MentionComposerProps> = ({
  onChange,
  onSubmit,
}) => {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const onSubmitRef = useRef(onSubmit)
  onSubmitRef.current = onSubmit

  // Escape belongs to the suggestion list while it's showing; only once it's gone
  // does Escape mean "throw this pin away".
  const suggestionShowing = useRef(false)

  const extensions = useMemo(
    () => [
      Document,
      Paragraph,
      Text,
      Placeholder.configure({ placeholder: 'Add feedback' }),
      Mention.configure({
        suggestion: {
          // As on the document-comment surface, so a full name can be typed out.
          // The cost it carries there too: a stray `@` — in an email address, say
          // — leaves the term open, so each keystroke to the end of the paragraph
          // searches again (debounced, six results).
          allowSpaces: true,
          items: userMentionItems(),
          render: createMentionSuggestionRenderer({
            onOpenChange: (open) => {
              suggestionShowing.current = open
            },
          }),
        },
      }),
    ],
    []
  )

  const editor = useEditor({
    extensions,
    autofocus: 'end',
    editorProps: {
      attributes: { role: 'textbox', 'aria-label': 'Feedback comment' },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Escape' && suggestionShowing.current) {
          // The suggestion plugin closes its own list; this only keeps the
          // keypress from also reaching the popover holding the draft pin.
          event.stopPropagation()
        }

        // Submit, as on the document-comment surface. These props are consulted
        // ahead of the plugins', so claiming the keypress here also keeps it
        // from the suggestion list — which takes plain Enter to pick a name.
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          onSubmitRef.current()
          return true
        }

        return false
      },
    },
    onUpdate: ({ editor: updated }) => {
      onChangeRef.current(serializeMentionText(updated))
    },
  })

  return (
    <div css={styles.composerFrame}>
      <EditorContent editor={editor} css={styles.composerContent} />
    </div>
  )
}
